import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'

// ── Types ─────────────────────────────────────────────────────
export interface ReportConfig {
  title: string
  subtitle?: string
  department?: string
  program?: string
  batchYear?: number | null
  preparedBy: string
  dateGenerated: string
  ucLogoDataUrl?: string
  trailblazeLogoDataUrl?: string
}

export interface ReportSection {
  title: string
  type: 'table' | 'text' | 'kpi' | 'heatmap'
  description?: string
  data?: { headers: string[]; rows: (string | number | null)[][] }
  text?: string
  kpis?: { label: string; value: string | number; unit?: string }[]
  chartOverride?: { labels: string[]; values: number[]; title: string }
  tableOptions?: { fontSize?: number; cellPadding?: number; columnStyles?: Record<number, { cellWidth?: number }> }
}

// ── Constants ─────────────────────────────────────────────────
const UNIVERSITY_NAME = 'University of Cebu Lapu-Lapu and Mandaue'
const UNIVERSITY_SHORT = 'UCLM'
const BLUE_PRIMARY: [number, number, number] = [30, 58, 95] // #1e3a5f
const GRAY_DARK: [number, number, number] = [55, 65, 81]
const GRAY_LIGHT: [number, number, number] = [156, 163, 175]
const MARGIN = 72 // 1 inch in points
const PAGE_WIDTH = 612 // Letter
const PAGE_HEIGHT = 792
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2

// ── PDF Generator ─────────────────────────────────────────────

// Truncate text to fit within maxWidth, adding ellipsis
function truncateText(doc: jsPDF, text: string, maxWidth: number): string {
  if (doc.getTextWidth(text) <= maxWidth) return text
  let truncated = text
  while (truncated.length > 0 && doc.getTextWidth(truncated + '...') > maxWidth) {
    truncated = truncated.slice(0, -1)
  }
  return truncated + '...'
}

export function generatePdfReport(
  config: ReportConfig,
  sections: ReportSection[],
  interventions?: { severity: string; category: string; program: string; message: string }[]
): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' })

  let pageCount = 0
  const pageNumbers: number[] = []

  const addPage = () => {
    if (pageCount > 0) doc.addPage()
    pageCount++
    pageNumbers.push(pageCount)
  }

  const addHeaderFooter = (pageNum: number) => {
    // Header
    doc.setFontSize(8)
    doc.setTextColor(...GRAY_LIGHT)
    doc.text(UNIVERSITY_SHORT, MARGIN, 40)
    doc.text(config.title, PAGE_WIDTH - MARGIN, 40, { align: 'right' })
    doc.setDrawColor(...GRAY_LIGHT)
    doc.setLineWidth(0.5)
    doc.line(MARGIN, 48, PAGE_WIDTH - MARGIN, 48)

    // Footer
    doc.setFontSize(7)
    doc.setTextColor(...GRAY_LIGHT)
    doc.text('Confidential — For Internal Use Only', MARGIN, PAGE_HEIGHT - 30)
    doc.text(`Page ${pageNum}`, PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 30, { align: 'right' })
  }

  // ═══ Cover Page ═══
  addPage()
  const centerX = PAGE_WIDTH / 2

  // ── Logos: UC logo left, Trailblaze logo right ──
  const LOGO_H = 72
  const LOGO_TOP = 65
  if (config.ucLogoDataUrl) {
    try {
      doc.addImage(config.ucLogoDataUrl, 'PNG', MARGIN, LOGO_TOP, LOGO_H, LOGO_H)
    } catch (_) { /* ignore if image fails */ }
  }
  if (config.trailblazeLogoDataUrl) {
    try {
      // Image is ~1334×967 px → ratio ≈ 1.38:1
      const tbH = 72
      const tbW = Math.round(tbH * (1334 / 967))
      doc.addImage(config.trailblazeLogoDataUrl, 'PNG', PAGE_WIDTH - MARGIN - tbW, LOGO_TOP, tbW, tbH)
    } catch (_) { /* ignore if image fails */ }
  }

  // ── Divider below logos ──
  doc.setDrawColor(...BLUE_PRIMARY)
  doc.setLineWidth(1)
  doc.line(MARGIN, 155, PAGE_WIDTH - MARGIN, 155)

  // University name
  doc.setFontSize(24)
  doc.setTextColor(...BLUE_PRIMARY)
  doc.setFont('times', 'bold')
  doc.text(UNIVERSITY_NAME, centerX, 230, { align: 'center' })

  // Separator line
  doc.setDrawColor(...BLUE_PRIMARY)
  doc.setLineWidth(2)
  doc.line(centerX - 120, 248, centerX + 120, 248)

  // Report title
  doc.setFontSize(20)
  doc.setTextColor(...GRAY_DARK)
  doc.text(config.title, centerX, 288, { align: 'center' })

  // Subtitle/scope
  if (config.subtitle) {
    doc.setFontSize(14)
    doc.setTextColor(...GRAY_LIGHT)
    doc.text(config.subtitle, centerX, 318, { align: 'center' })
  }

  // Scope info
  let scopeY = 368
  doc.setFontSize(11)
  doc.setTextColor(...GRAY_DARK)
  doc.setFont('times', 'normal')

  if (config.department) {
    doc.text(`Department: ${config.department}`, centerX, scopeY, { align: 'center' })
    scopeY += 22
  }
  if (config.program) {
    doc.text(`Program: ${config.program}`, centerX, scopeY, { align: 'center' })
    scopeY += 22
  }
  if (config.batchYear) {
    doc.text(`Batch Year: Class of ${config.batchYear}`, centerX, scopeY, { align: 'center' })
    scopeY += 22
  }

  // Prepared by
  scopeY += 40
  doc.setFontSize(10)
  doc.text(`Prepared by: ${config.preparedBy}`, centerX, scopeY, { align: 'center' })
  doc.text(`Date Generated: ${config.dateGenerated}`, centerX, scopeY + 20, { align: 'center' })

  // ═══ Table of Contents ═══
  const tocPageNum = pageCount + 1
  addPage()
  addHeaderFooter(pageCount)

  doc.setFontSize(18)
  doc.setTextColor(...BLUE_PRIMARY)
  doc.setFont('times', 'bold')
  doc.text('Table of Contents', MARGIN, 80)

  doc.setDrawColor(...BLUE_PRIMARY)
  doc.setLineWidth(1)
  doc.line(MARGIN, 90, MARGIN + 180, 90)

  // Track section page numbers for later
  const sectionPageNumbers: number[] = []

  // Placeholder TOC entries - will be redrawn with real page numbers after rendering
  let tocY = 120
  const TOC_TEXT_MAX_WIDTH = CONTENT_WIDTH - 40 // leave room for page number
  doc.setFontSize(10)
  doc.setFont('times', 'normal')
  doc.setTextColor(...GRAY_DARK)

  sections.forEach((section, i) => {
    const label = `${i + 1}. ${section.title}`
    const truncated = truncateText(doc, label, TOC_TEXT_MAX_WIDTH)
    doc.text(truncated, MARGIN + 10, tocY)
    tocY += 16
    if (tocY > PAGE_HEIGHT - 60) { addPage(); addHeaderFooter(pageCount); tocY = 80 }
  })

  if (interventions && interventions.length > 0) {
    const label = `${sections.length + 1}. Findings & Recommendations`
    doc.text(label, MARGIN + 10, tocY)
  }
  // ═══ Content Sections ═══
  let sectionNum = 0

  sections.forEach(section => {
    sectionNum++
    addPage()
    addHeaderFooter(pageCount)
    
    // Track this section's page number
    sectionPageNumbers.push(pageCount)

    let y = 80

    // Section header — wrap long titles
    doc.setFontSize(14)
    doc.setTextColor(...BLUE_PRIMARY)
    doc.setFont('times', 'bold')
    const headerLines = doc.splitTextToSize(`${sectionNum}.0  ${section.title}`, CONTENT_WIDTH)
    doc.text(headerLines, MARGIN, y)
    y += headerLines.length * 16 + 2
    doc.setDrawColor(...BLUE_PRIMARY)
    doc.setLineWidth(0.75)
    doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y)
    y += 16

    // KPI section
    if (section.type === 'kpi' && section.kpis) {
      const LABEL_COL_W = CONTENT_WIDTH * 0.72
      const VALUE_COL_X = MARGIN + 10 + LABEL_COL_W + 8
      section.kpis.forEach(kpi => {
        const labelText = kpi.label.replace(/≥/g, '>=').replace(/≤/g, '<=')
        doc.setFontSize(10)
        doc.setTextColor(...GRAY_LIGHT)
        doc.setFont('times', 'normal')
        const labelLines = doc.splitTextToSize(labelText, LABEL_COL_W - 8)
        const rowH = Math.max(labelLines.length * 13, 20)

        // Check page overflow
        if (y + rowH + 6 > PAGE_HEIGHT - 60) {
          addPage()
          addHeaderFooter(pageCount)
          y = 80
        }

        doc.text(labelLines, MARGIN + 10, y)

        doc.setFontSize(14)
        doc.setTextColor(...BLUE_PRIMARY)
        doc.setFont('times', 'bold')
        doc.text(`${kpi.value}${kpi.unit || ''}`, VALUE_COL_X, y)
        y += rowH + 4
      })

      // Draw distribution bar chart if embedded in kpi data
      if ((section as any).distribution) {
        y += 10
        doc.setFontSize(10)
        doc.setFont('times', 'bold')
        doc.setTextColor(...BLUE_PRIMARY)
        doc.text('Distribution', MARGIN + 10, y)
        y += 14

        const dist = (section as any).distribution as { label: string; count: number; pct: number }[]
        const maxCount = Math.max(...dist.map(d => d.count), 1)
        const BAR_MAX_WIDTH = 200
        const BAR_HEIGHT = 12

        dist.forEach(item => {
          // Label
          doc.setFontSize(9)
          doc.setFont('times', 'normal')
          doc.setTextColor(...GRAY_DARK)
          const labelStr = String(item.label).padStart(2, ' ')
          doc.text(labelStr, MARGIN + 14, y + 9)

          // Bar background
          const barX = MARGIN + 50
          doc.setFillColor(230, 230, 235)
          doc.rect(barX, y, BAR_MAX_WIDTH, BAR_HEIGHT, 'F')

          // Bar fill
          const barWidth = Math.max((item.count / maxCount) * BAR_MAX_WIDTH, item.count > 0 ? 4 : 0)
          doc.setFillColor(...BLUE_PRIMARY)
          doc.rect(barX, y, barWidth, BAR_HEIGHT, 'F')

          // Count + percentage text
          doc.setFontSize(8)
          const labelStr2 = `${item.count}  (${item.pct.toFixed(1)}%)`
          const labelW2 = doc.getTextWidth(labelStr2)
          if (barWidth >= labelW2 + 8) {
            // Wide enough — draw inside bar in white
            doc.setTextColor(255, 255, 255)
            doc.text(labelStr2, barX + barWidth - 4, y + 9, { align: 'right' })
          } else {
            // Narrow — draw outside bar in dark
            doc.setTextColor(...GRAY_DARK)
            doc.text(labelStr2, barX + barWidth + 5, y + 9)
          }

          y += BAR_HEIGHT + 4
        })
      }
    }

    // Table section
    if (section.type === 'table' && section.data) {
      const tblFontSize = section.tableOptions?.fontSize ?? 9
      const tblCellPadding = section.tableOptions?.cellPadding ?? 6
      const tblColumnStyles = section.tableOptions?.columnStyles
      autoTable(doc, {
        startY: y,
        head: [section.data.headers],
        body: section.data.rows.map(row => row.map(cell => cell === null ? 'N/A' : String(cell))),
        margin: { top: 60, left: MARGIN, right: MARGIN },
        styles: {
          fontSize: tblFontSize,
          cellPadding: tblCellPadding,
          font: 'times',
          overflow: 'linebreak',
        },
        headStyles: {
          fillColor: BLUE_PRIMARY,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: tblFontSize,
          font: 'times',
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        ...(tblColumnStyles ? { columnStyles: tblColumnStyles } : {}),
        theme: 'grid',
        didDrawPage: (_data: any) => {
          // Sync pageCount with actual pages so subsequent sections get correct page numbers
          const realPages = doc.getNumberOfPages()
          if (realPages > pageCount) {
            // autoTable added new page(s) — add header/footer to them
            for (let p = pageCount + 1; p <= realPages; p++) {
              doc.setPage(p)
              addHeaderFooter(p)
            }
            pageCount = realPages
            // Go back to current page so autoTable continues correctly
            doc.setPage(realPages)
          }
        },
      })
    }

    // Heatmap section — color-coded score matrix
    if (section.type === 'heatmap' && section.data) {
      if (section.description) {
        doc.setFontSize(9)
        doc.setTextColor(...GRAY_LIGHT)
        doc.setFont('times', 'italic')
        const descLines = doc.splitTextToSize(section.description.replace(/≥/g, '>=').replace(/≤/g, '<='), CONTENT_WIDTH - 20)
        doc.text(descLines, MARGIN + 10, y)
        y += descLines.length * 13 + 8
      }

      const heatRows = section.data.rows
      autoTable(doc, {
        startY: y,
        head: [section.data.headers],
        body: heatRows.map(row => row.map(cell => cell === null ? 'N/A' : String(cell))),
        margin: { top: 60, left: MARGIN, right: MARGIN },
        styles: { fontSize: 8, cellPadding: 5, font: 'times', halign: 'center' },
        headStyles: {
          fillColor: BLUE_PRIMARY,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 8,
          font: 'times',
        },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
        theme: 'grid',
        didParseCell: (data: any) => {
          if (data.section === 'body' && data.column.index > 0) {
            const rawVal = heatRows[data.row.index]?.[data.column.index]
            if (rawVal === null || rawVal === undefined) {
              data.cell.styles.fillColor = [243, 244, 246]
              data.cell.styles.textColor = [156, 163, 175]
            } else {
              const score = Number(rawVal)
              if (score >= 70) {
                data.cell.styles.fillColor = [220, 252, 231]
                data.cell.styles.textColor = [21, 128, 61]
              } else if (score >= 50) {
                data.cell.styles.fillColor = [254, 249, 195]
                data.cell.styles.textColor = [146, 64, 14]
              } else {
                data.cell.styles.fillColor = [254, 226, 226]
                data.cell.styles.textColor = [185, 28, 28]
              }
            }
          }
        },
        didDrawPage: (_data: any) => {
          const realPages = doc.getNumberOfPages()
          if (realPages > pageCount) {
            for (let p = pageCount + 1; p <= realPages; p++) {
              doc.setPage(p)
              addHeaderFooter(p)
            }
            pageCount = realPages
            doc.setPage(realPages)
          }
        },
      })

      // Legend
      const legendY = (doc as any).lastAutoTable.finalY + 14
      doc.setFontSize(8)
      doc.setFont('times', 'normal')
      doc.setTextColor(...GRAY_DARK)
      const legend = [
        { color: [220, 252, 231] as [number,number,number], text: '>=70 (Met)', x: MARGIN + 10 },
        { color: [254, 249, 195] as [number,number,number], text: '50-69 (Partial)', x: MARGIN + 90 },
        { color: [254, 226, 226] as [number,number,number], text: '<50 (Needs Work)', x: MARGIN + 195 },
        { color: [243, 244, 246] as [number,number,number], text: 'N/A', x: MARGIN + 315 },
      ]
      legend.forEach(({ color, text, x }) => {
        doc.setFillColor(...color)
        doc.rect(x, legendY - 8, 10, 10, 'F')
        doc.setDrawColor(200, 200, 200)
        doc.rect(x, legendY - 8, 10, 10, 'S')
        doc.text(text, x + 14, legendY)
      })
    }

    // Text section — for multiple choice / checkbox bars
    if (section.type === 'text') {
      // Draw horizontal bar chart if embedded
      if ((section as any).barChart) {
        const bars = (section as any).barChart as { label: string; count: number; pct: number }[]
        const total = (section as any).totalResponses || 0

        doc.setFontSize(9)
        doc.setFont('times', 'normal')
        doc.setTextColor(...GRAY_DARK)
        doc.text(`Total Responses: ${total}`, MARGIN + 10, y)
        y += 18

        const maxCount = Math.max(...bars.map(b => b.count), 1)
        const BAR_MAX_WIDTH = 180
        const BAR_HEIGHT = 14
        const LABEL_WIDTH = 200

        bars.forEach(item => {
          // Wrap long labels
          const labelLines = doc.splitTextToSize(item.label, LABEL_WIDTH - 10)
          const rowHeight = Math.max(BAR_HEIGHT, labelLines.length * 11)

          // Check page overflow
          if (y + rowHeight + 6 > PAGE_HEIGHT - 60) {
            addPage()
            addHeaderFooter(pageCount)
            y = 80
          }

          // Label
          doc.setFontSize(9)
          doc.setFont('times', 'normal')
          doc.setTextColor(...GRAY_DARK)
          doc.text(labelLines, MARGIN + 10, y + 10)

          // Bar background
          const barX = MARGIN + LABEL_WIDTH
          doc.setFillColor(230, 230, 235)
          doc.rect(barX, y + 1, BAR_MAX_WIDTH, BAR_HEIGHT, 'F')

          // Bar fill
          const barWidth = Math.max((item.count / maxCount) * BAR_MAX_WIDTH, item.count > 0 ? 4 : 0)
          doc.setFillColor(...BLUE_PRIMARY)
          doc.rect(barX, y + 1, barWidth, BAR_HEIGHT, 'F')

          // Count + percentage
          doc.setFontSize(8)
          const labelStr3 = `${item.count}  (${item.pct.toFixed(1)}%)`
          const labelW3 = doc.getTextWidth(labelStr3)
          if (barWidth >= labelW3 + 8) {
            // Wide enough — draw inside bar in white
            doc.setTextColor(255, 255, 255)
            doc.text(labelStr3, barX + barWidth - 4, y + 10, { align: 'right' })
          } else {
            // Narrow — draw outside bar in dark
            doc.setTextColor(...GRAY_DARK)
            doc.text(labelStr3, barX + barWidth + 5, y + 10)
          }

          y += rowHeight + 6
        })
      } else if (section.text) {
        doc.setFontSize(10)
        doc.setTextColor(...GRAY_DARK)
        doc.setFont('times', 'normal')
        const lines = doc.splitTextToSize(section.text, CONTENT_WIDTH)
        doc.text(lines, MARGIN + 10, y)
      }
    }
  })

  // ═══ Interventions / Recommendations ═══
  let interventionsPageNum = 0
  if (interventions && interventions.length > 0) {
    sectionNum++
    addPage()
    addHeaderFooter(pageCount)
    interventionsPageNum = pageCount

    let y = 80
    doc.setFontSize(16)
    doc.setTextColor(...BLUE_PRIMARY)
    doc.setFont('times', 'bold')
    doc.text(`${sectionNum}.0  Findings & Recommendations`, MARGIN, y)
    y += 8
    doc.setDrawColor(...BLUE_PRIMARY)
    doc.setLineWidth(0.75)
    doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y)
    y += 20

    interventions.forEach((item, idx) => {
      if (y > PAGE_HEIGHT - 120) {
        addPage()
        addHeaderFooter(pageCount)
        y = 80
      }

      const severityLabel = item.severity.toUpperCase()
      doc.setFontSize(10)
      doc.setFont('times', 'bold')
      doc.setTextColor(
        item.severity === 'critical' ? 200 : item.severity === 'warning' ? 180 : 59,
        item.severity === 'critical' ? 30 : item.severity === 'warning' ? 130 : 130,
        item.severity === 'critical' ? 30 : item.severity === 'warning' ? 0 : 246
      )
      doc.text(`${idx + 1}. [${severityLabel}]`, MARGIN + 10, y)

      doc.setFont('times', 'normal')
      doc.setTextColor(...GRAY_DARK)
      const msgLines = doc.splitTextToSize(item.message, CONTENT_WIDTH - 30)
      doc.text(msgLines, MARGIN + 20, y + 14)
      y += 14 + msgLines.length * 13 + 10
    })
  }

  // Go back and update TOC with actual page numbers
  // We may have multiple TOC pages, redraw them all
  tocY = 120
  let tocPageIdx = tocPageNum
  doc.setPage(tocPageIdx)

  // Clear entire TOC content area on first TOC page
  doc.setFillColor(255, 255, 255)
  doc.rect(MARGIN, 100, CONTENT_WIDTH, PAGE_HEIGHT - 160, 'F')

  // Draw "Page" column header
  doc.setFontSize(9)
  doc.setFont('times', 'bold')
  doc.setTextColor(...BLUE_PRIMARY)
  doc.text('Page', PAGE_WIDTH - MARGIN - 10, 108, { align: 'right' })

  doc.setFontSize(10)
  doc.setFont('times', 'normal')

  sections.forEach((section, i) => {
    if (tocY > PAGE_HEIGHT - 60) {
      tocPageIdx++
      doc.setPage(tocPageIdx)
      doc.setFillColor(255, 255, 255)
      doc.rect(MARGIN, 55, CONTENT_WIDTH, PAGE_HEIGHT - 100, 'F')
      tocY = 80
    }

    const pageNum = sectionPageNumbers[i]
    const label = `${i + 1}. ${section.title}`
    const truncated = truncateText(doc, label, TOC_TEXT_MAX_WIDTH)
    const dots = '.' .repeat(100)
    const pageStr = `${pageNum - 1}`

    doc.setTextColor(...GRAY_DARK)
    doc.setFont('times', 'normal')
    // Draw dots first (will be covered by text)
    doc.setTextColor(180, 180, 180)
    doc.text(dots, MARGIN + 10, tocY)
    // Draw white background behind text
    const textWidth = doc.getTextWidth(truncated)
    doc.setFillColor(255, 255, 255)
    doc.rect(MARGIN + 8, tocY - 10, textWidth + 4, 14, 'F')
    // Draw the actual title
    doc.setTextColor(...GRAY_DARK)
    doc.text(truncated, MARGIN + 10, tocY)
    // Draw white bg behind page number
    const pageNumWidth = doc.getTextWidth(pageStr)
    doc.setFillColor(255, 255, 255)
    doc.rect(PAGE_WIDTH - MARGIN - pageNumWidth - 14, tocY - 10, pageNumWidth + 16, 14, 'F')
    // Draw page number
    doc.text(pageStr, PAGE_WIDTH - MARGIN - 10, tocY, { align: 'right' })

    tocY += 16
  })

  if (interventions && interventions.length > 0) {
    if (tocY > PAGE_HEIGHT - 60) {
      tocPageIdx++
      doc.setPage(tocPageIdx)
      tocY = 80
    }
    const label = `${sections.length + 1}. Findings & Recommendations`
    const pageStr = `${interventionsPageNum - 1}`
    doc.setTextColor(180, 180, 180)
    doc.text('.'.repeat(100), MARGIN + 10, tocY)
    const textWidth = doc.getTextWidth(label)
    doc.setFillColor(255, 255, 255)
    doc.rect(MARGIN + 8, tocY - 10, textWidth + 4, 14, 'F')
    doc.setTextColor(...GRAY_DARK)
    doc.text(label, MARGIN + 10, tocY)
    const pageNumWidth = doc.getTextWidth(pageStr)
    doc.setFillColor(255, 255, 255)
    doc.rect(PAGE_WIDTH - MARGIN - pageNumWidth - 14, tocY - 10, pageNumWidth + 16, 14, 'F')
    doc.text(pageStr, PAGE_WIDTH - MARGIN - 10, tocY, { align: 'right' })
  }

  // Go back and fix page numbers (update total count)
  // Use doc.getNumberOfPages() to get the REAL total (including auto-added pages from autoTable)
  const totalPages = doc.getNumberOfPages()
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i)
    // Overwrite footer with correct total
    doc.setFillColor(255, 255, 255)
    doc.rect(PAGE_WIDTH - MARGIN - 80, PAGE_HEIGHT - 38, 82, 14, 'F')
    doc.setFontSize(7)
    doc.setTextColor(...GRAY_LIGHT)
    doc.text(`Page ${i - 1} of ${totalPages - 1}`, PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 30, { align: 'right' })
  }

  return doc
}

const CHART_WIDTH = 960
const SAFE_EXCEL_NAME = /[:\\/?*\[\]]/g
const SHEET_NAME_MAX = 31

const excelTitleStyle = {
  font: { name: 'Calibri', size: 18, bold: true, color: { argb: 'FF1E3A5F' } },
  alignment: { horizontal: 'left' as const, vertical: 'middle' as const },
}
const excelSubtitleStyle = {
  font: { name: 'Calibri', size: 11, color: { argb: 'FF64748B' } },
  alignment: { horizontal: 'left' as const, vertical: 'middle' as const },
}
const excelHeaderStyle = {
  font: { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } },
  fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF1E3A5F' } },
  alignment: { horizontal: 'center' as const, vertical: 'middle' as const, wrapText: true as const },
  border: {
    top: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } },
    left: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } },
    bottom: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } },
    right: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } },
  },
}
const excelRowStyle = (alt = false) => ({
  font: { name: 'Calibri', size: 11, color: { argb: 'FF1E293B' } },
  fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: alt ? 'FFF1F5F9' : 'FFFFFFFF' } },
  alignment: { vertical: 'top' as const, wrapText: true as const },
  border: {
    left: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } },
    right: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } },
    bottom: { style: 'hair' as const, color: { argb: 'FFE2E8F0' } },
  },
})

const excelSeverityColors: Record<string, string> = {
  critical: 'FFDC2626',
  warning: 'FFD97706',
  info: 'FF2563EB',
}

function safeSheetName(name: string) {
  return name.replace(SAFE_EXCEL_NAME, '-').substring(0, SHEET_NAME_MAX)
}

function createCanvas(width: number, height: number) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

function renderBarChartImage(title: string, labels: string[], values: number[]): { base64: string; height: number } {
  const BAR_H = 28
  const GAP = 10
  const BOTTOM_PAD = 16
  const TITLE_FONT = 'bold 16px Arial'
  const TITLE_LINE_H = 22
  const MAX_TITLE_W = CHART_WIDTH - 48

  // Word-wrap the title on a temp canvas to measure
  const tmpCvs = createCanvas(CHART_WIDTH, 100)
  const tmpCtx = tmpCvs.getContext('2d')!
  tmpCtx.font = TITLE_FONT
  const titleLines: string[] = []
  const words = title.split(' ')
  let currentLine = ''
  for (const word of words) {
    const test = currentLine ? `${currentLine} ${word}` : word
    if (tmpCtx.measureText(test).width > MAX_TITLE_W && currentLine) {
      titleLines.push(currentLine)
      currentLine = word
    } else {
      currentLine = test
    }
  }
  if (currentLine) titleLines.push(currentLine)

  const CHART_TOP = 12 + titleLines.length * TITLE_LINE_H + 12
  const canvasHeight = Math.max(200, CHART_TOP + labels.length * (BAR_H + GAP) + BOTTOM_PAD)

  const canvas = createCanvas(CHART_WIDTH, canvasHeight)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not supported')

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, CHART_WIDTH, canvasHeight)

  ctx.fillStyle = '#1E3A5F'
  ctx.font = TITLE_FONT
  ctx.textAlign = 'left'
  titleLines.forEach((line, i) => {
    ctx.fillText(line, 24, 12 + (i + 1) * TITLE_LINE_H)
  })

  const chartLeft = 24
  const chartTop = CHART_TOP
  const chartWidth = CHART_WIDTH - 48
  const chartHeight = canvasHeight - chartTop - BOTTOM_PAD
  const maxValue = Math.max(...values, 1)

  labels.forEach((label, index) => {
    const value = values[index]
    const y = chartTop + index * (BAR_H + GAP)
    const barWidth = Math.max(4, Math.round((value / maxValue) * (chartWidth * 0.60)))

    ctx.fillStyle = '#E2E8F0'
    ctx.fillRect(chartLeft, y, chartWidth * 0.60, BAR_H)

    ctx.fillStyle = index % 2 === 0 ? '#2563EB' : '#1D4ED8'
    ctx.fillRect(chartLeft, y, barWidth, BAR_H)

    // Value label: white inside bar if wide enough, dark outside if narrow
    const valueStr = String(value)
    ctx.font = 'bold 12px Arial'
    const valueW = ctx.measureText(valueStr).width
    const textY = y + BAR_H / 2 + 4
    if (barWidth >= valueW + 12) {
      ctx.fillStyle = '#FFFFFF'
      ctx.textAlign = 'right'
      ctx.fillText(valueStr, chartLeft + barWidth - 6, textY)
    } else {
      ctx.fillStyle = '#1E3A5F'
      ctx.textAlign = 'left'
      ctx.fillText(valueStr, chartLeft + barWidth + 6, textY)
    }

    ctx.fillStyle = '#0F172A'
    ctx.font = 'normal 12px Arial'
    ctx.textAlign = 'left'
    ctx.fillText(label, chartLeft + chartWidth * 0.62 + 8, y + BAR_H / 2 + 4)
  })

  ctx.strokeStyle = '#CBD5E1'
  ctx.lineWidth = 1
  ctx.strokeRect(chartLeft, chartTop, chartWidth * 0.60, chartHeight)

  return { base64: canvas.toDataURL('image/png'), height: canvasHeight }
}

interface ChartDefinition {
  title: string
  description?: string
  imageBase64: string
  imageHeight: number
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function addChartSheet(workbook: ExcelJS.Workbook, charts: ChartDefinition[]) {
  const sheet = workbook.addWorksheet('Summary Charts')
  sheet.columns = [{ width: 130 }]
  let rowIndex = 1

  charts.forEach(chart => {
    const titleRow = sheet.getRow(rowIndex)
    titleRow.height = 28
    titleRow.getCell(1).value = chart.title
    titleRow.getCell(1).font = { name: 'Calibri', size: 15, bold: true, color: { argb: 'FF1E3A5F' } }
    rowIndex++

    if (chart.description) {
      const descRow = sheet.getRow(rowIndex)
      descRow.height = 18
      descRow.getCell(1).value = chart.description
      descRow.getCell(1).font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF64748B' } }
      rowIndex++
    }

    rowIndex++ // gap before image

    const displayWidth = Math.round(CHART_WIDTH * 0.85)
    const displayHeight = Math.round(chart.imageHeight * 0.85)
    const imageId = workbook.addImage({ base64: chart.imageBase64.replace(/^data:image\/png;base64,/, ''), extension: 'png' })
    sheet.addImage(imageId, {
      tl: { col: 0, row: rowIndex - 1 },
      ext: { width: displayWidth, height: displayHeight },
    })
    rowIndex += Math.ceil(displayHeight / 15) + 4
  })
}

function columnWidthFromValues(headers: string[], rows: (string | number | null)[][]) {
  return headers.map((header, idx) => {
    const maxLength = rows.reduce((max, row) => {
      const value = row[idx]
      const len = value == null ? 0 : String(value).length
      return Math.max(max, len)
    }, header.length)
    return { width: Math.min(Math.max(maxLength + 6, 12), 50) }
  })
}

function addInfoSheet(workbook: ExcelJS.Workbook, config: ReportConfig) {
  const sheet = workbook.addWorksheet('Report Info')
  sheet.columns = [
    { header: '', width: 28 },
    { header: '', width: 34 },
    { header: '', width: 28 },
    { header: '', width: 24 },
  ]
  sheet.mergeCells('A1:D1')
  sheet.mergeCells('A2:D2')
  sheet.mergeCells('A4:D4')

  const titleRow = sheet.getRow(1)
  titleRow.getCell(1).value = UNIVERSITY_NAME
  titleRow.height = 30
  titleRow.getCell(1).style = excelTitleStyle

  const subtitleRow = sheet.getRow(2)
  subtitleRow.getCell(1).value = config.title
  subtitleRow.height = 22
  subtitleRow.getCell(1).style = excelSubtitleStyle

  const labelRow = sheet.getRow(4)
  labelRow.getCell(1).value = 'Report Details'
  labelRow.height = 20
  labelRow.getCell(1).font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FF1E3A5F' } }

  const details = [
    ['Prepared by', config.preparedBy],
    ['Date Generated', config.dateGenerated],
    ['Department', config.department || 'All Departments'],
    ['Program', config.program || 'All Programs'],
    ['Batch Year', config.batchYear ? `Class of ${config.batchYear}` : 'All Years'],
  ]

  details.forEach((detail, index) => {
    const row = sheet.getRow(5 + index)
    row.getCell(1).value = detail[0]
    row.getCell(2).value = detail[1]
    row.getCell(1).font = { name: 'Calibri', bold: true, color: { argb: 'FF1E3A5F' } }
    row.getCell(2).font = { name: 'Calibri', color: { argb: 'FF475569' } }
    row.height = 18
  })
}

export function downloadPdf(doc: jsPDF, filename: string) {
  doc.save(filename)
}

export async function generateExcelReport(
  config: ReportConfig,
  sections: ReportSection[],
  interventions?: { severity: string; category: string; program: string; message: string }[]
): Promise<ExcelJS.Workbook> {
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'Trailblaze'
  workbook.created = new Date()

  addInfoSheet(workbook, config)

  for (const section of sections) {
    const sheetName = safeSheetName(section.title)
    const sheet = workbook.addWorksheet(sheetName, {
      views: [{ state: 'frozen', xSplit: 0, ySplit: 4, topLeftCell: 'A5' }],
    })

    const headerCount = Math.max(section.data?.headers.length ?? 2, 2)
    const lastCol = String.fromCharCode(65 + headerCount - 1)
    sheet.mergeCells(`A1:${lastCol}1`)
    sheet.getRow(1).getCell(1).value = UNIVERSITY_NAME
    sheet.getRow(1).height = 28
    sheet.getRow(1).getCell(1).style = excelTitleStyle

    sheet.mergeCells(`A2:${lastCol}2`)
    sheet.getRow(2).getCell(1).value = section.title
    sheet.getRow(2).height = 20
    sheet.getRow(2).getCell(1).style = excelSubtitleStyle
    sheet.getRow(3).height = 8

    if (section.type === 'table' && section.data) {
      sheet.columns = columnWidthFromValues(section.data.headers, section.data.rows)
      const headerRow = sheet.addRow(section.data.headers)
      headerRow.eachCell(cell => { cell.style = excelHeaderStyle })

      section.data.rows.forEach((rowValues, rowIndex) => {
        const row = sheet.addRow(rowValues)
        const style = excelRowStyle(rowIndex % 2 === 1)
        row.eachCell(cell => { cell.style = style })
      })

      if (section.chartOverride) {
        const { labels: chartLabels, values: chartValues, title: chartTitle } = section.chartOverride
        if (chartLabels.length && chartValues.some(v => !Number.isNaN(v))) {
          const chart = renderBarChartImage(chartTitle, chartLabels, chartValues)
          sheet.addRow([])
          const chartStartRow = sheet.rowCount + 1
          const displayW = Math.round(CHART_WIDTH * 0.85)
          const displayH = Math.round(chart.height * 0.85)
          const imageId = workbook.addImage({ base64: chart.base64.replace(/^data:image\/png;base64,/, ''), extension: 'png' })
          sheet.addImage(imageId, { tl: { col: 0, row: chartStartRow - 1 }, ext: { width: displayW, height: displayH } })
          const blankRows = Math.ceil(displayH / 15) + 2
          for (let i = 0; i < blankRows; i++) sheet.addRow([])
        }
      } else {
        const numericIndex = section.data.headers.findIndex((_, idx) =>
          section.data!.rows.some(row => typeof row[idx] === 'number')
        )
        if (numericIndex >= 0) {
          const col0Values = section.data.rows.map(row => String(row[0] || ''))
          const col0UniqueCount = new Set(col0Values).size
          const rowCount = section.data.rows.length
          let chartLabels: string[]
          let chartValues: number[]
          if (col0UniqueCount === rowCount) {
            // Every row is unique — use col0 as label directly
            chartLabels = col0Values
            chartValues = section.data.rows.map(row => Number(row[numericIndex] || 0))
          } else if (col0UniqueCount === 1) {
            // Single program — use col1+col2 as label (e.g. "PEO1", "PO2")
            chartLabels = section.data.rows.map(row =>
              [String(row[1] || ''), String(row[2] || '')].filter(Boolean).join('')
            )
            chartValues = section.data.rows.map(row => Number(row[numericIndex] || 0))
          } else {
            // Multiple programs with repeated rows — aggregate: average score per program
            const aggregateMap = new Map<string, { sum: number; count: number }>()
            section.data.rows.forEach(row => {
              const key = String(row[0] || '')
              const val = Number(row[numericIndex] || 0)
              if (!aggregateMap.has(key)) aggregateMap.set(key, { sum: 0, count: 0 })
              const entry = aggregateMap.get(key)!
              entry.sum += val
              entry.count += 1
            })
            chartLabels = Array.from(aggregateMap.keys())
            chartValues = Array.from(aggregateMap.values()).map(e =>
              Math.round((e.sum / e.count) * 10) / 10
            )
          }
          if (chartLabels.length && chartValues.some(v => !Number.isNaN(v))) {
            const chart = renderBarChartImage(`${section.title} Overview`, chartLabels, chartValues)
            sheet.addRow([])
            if (section.description) {
              const descRow = sheet.addRow([section.description])
              descRow.getCell(1).font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF64748B' } }
            }
            const chartStartRow = sheet.rowCount + 1
            const displayW = Math.round(CHART_WIDTH * 0.85)
            const displayH = Math.round(chart.height * 0.85)
            const imageId = workbook.addImage({ base64: chart.base64.replace(/^data:image\/png;base64,/, ''), extension: 'png' })
            sheet.addImage(imageId, { tl: { col: 0, row: chartStartRow - 1 }, ext: { width: displayW, height: displayH } })
            const blankRows = Math.ceil(displayH / 15) + 2
            for (let i = 0; i < blankRows; i++) sheet.addRow([])
          }
        }
      }
    }

    if (section.type === 'kpi' && section.kpis) {
      sheet.columns = [{ width: 48 }, { width: 18 }]
      const headerRow = sheet.addRow(['Metric', 'Value'])
      headerRow.eachCell(cell => { cell.style = excelHeaderStyle })

      section.kpis.forEach((kpi, index) => {
        const row = sheet.addRow([kpi.label, `${kpi.value}${kpi.unit || ''}`])
        row.getCell(1).style = excelRowStyle(index % 2 === 1)
        row.getCell(2).style = { ...excelRowStyle(index % 2 === 1), font: { name: 'Calibri', size: 13, bold: true, color: { argb: 'FF2563EB' } }, alignment: { horizontal: 'center' as const, vertical: 'middle' as const } }
      })

      const labels = section.kpis.map(k => k.label)
      const values = section.kpis.map(k => Number(k.value || 0))
      if (values.some(v => !Number.isNaN(v))) {
        const chart = renderBarChartImage(`${section.title} Overview`, labels, values)
        sheet.addRow([])
        if (section.description) {
          const descRow = sheet.addRow([section.description])
          descRow.getCell(1).font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF64748B' } }
        }
        const chartStartRow = sheet.rowCount + 1
        const displayW = Math.round(CHART_WIDTH * 0.85)
        const displayH = Math.round(chart.height * 0.85)
        const imageId = workbook.addImage({ base64: chart.base64.replace(/^data:image\/png;base64,/, ''), extension: 'png' })
        sheet.addImage(imageId, { tl: { col: 0, row: chartStartRow - 1 }, ext: { width: displayW, height: displayH } })
        const blankRows = Math.ceil(displayH / 15) + 2
        for (let i = 0; i < blankRows; i++) sheet.addRow([])
      }
    }

    if (section.type === 'heatmap' && section.data) {
      const { headers, rows } = section.data
      const safeRows = rows.map(r => r.map(c => c === null ? 'N/A' : c) as (string | number)[])
      sheet.columns = columnWidthFromValues(headers, safeRows)
      const headerRow = sheet.addRow(headers)
      headerRow.eachCell(cell => { cell.style = excelHeaderStyle })

      rows.forEach((rowValues, rowIndex) => {
        const displayRow = sheet.addRow(rowValues.map(c => c === null ? 'N/A' : c))
        rowValues.forEach((val, colIdx) => {
          const cell = displayRow.getCell(colIdx + 1)
          if (colIdx === 0) {
            cell.style = { ...excelRowStyle(rowIndex % 2 === 1), font: { name: 'Calibri', bold: true, color: { argb: 'FF1E3A5F' } } }
          } else if (val === null) {
            cell.style = {
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF3F4F6' } },
              font: { name: 'Calibri', color: { argb: 'FF9CA3AF' }, italic: true },
              alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
            }
          } else {
            const score = Number(val)
            const fgColor = score >= 70 ? 'FFDCFCE7' : score >= 50 ? 'FFFEF9C3' : 'FFFEE2E2'
            const textColor = score >= 70 ? 'FF15803D' : score >= 50 ? 'FF92400E' : 'FFB91C1C'
            cell.style = {
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fgColor } },
              font: { name: 'Calibri', bold: true, color: { argb: textColor } },
              alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
            }
          }
        })
      })

      // Legend row
      sheet.addRow([])
      const legendRow = sheet.addRow(['Legend:', '≥70 Met', '50–69 Partial', '<50 Needs Work', 'N/A'])
      legendRow.getCell(1).font = { name: 'Calibri', bold: true, color: { argb: 'FF475569' } }
      const legendColors = ['FFDCFCE7', 'FFFEF9C3', 'FFFEE2E2', 'FFF3F4F6']
      const legendText = ['FF15803D', 'FF92400E', 'FFB91C1C', 'FF9CA3AF']
      legendColors.forEach((argb, i) => {
        const cell = legendRow.getCell(i + 2)
        cell.style = {
          fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb } },
          font: { name: 'Calibri', bold: true, color: { argb: legendText[i] } },
          alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
        }
      })
    }
  }

  if (interventions && interventions.length > 0) {
    const sheet = workbook.addWorksheet('Recommendations')
    sheet.columns = [{ width: 6 }, { width: 14 }, { width: 18 }, { width: 18 }, { width: 70 }]
    sheet.mergeCells('A1:E1')
    sheet.getRow(1).getCell(1).value = UNIVERSITY_NAME
    sheet.getRow(1).height = 28
    sheet.getRow(1).getCell(1).style = excelTitleStyle
    sheet.mergeCells('A2:E2')
    sheet.getRow(2).getCell(1).value = 'Findings & Recommendations'
    sheet.getRow(2).height = 20
    sheet.getRow(2).getCell(1).style = excelSubtitleStyle
    sheet.getRow(3).height = 8

    const headerRow = sheet.addRow(['#', 'Severity', 'Category', 'Program', 'Recommendation'])
    headerRow.eachCell(cell => { cell.style = excelHeaderStyle })

    interventions.forEach((item, index) => {
      const row = sheet.addRow([
        index + 1,
        item.severity.toUpperCase(),
        item.category,
        item.program,
        item.message,
      ])
      row.getCell(1).style = excelRowStyle(index % 2 === 1)
      row.getCell(2).style = { ...excelRowStyle(index % 2 === 1), font: { name: 'Calibri', size: 11, bold: true, color: { argb: excelSeverityColors[item.severity] || 'FF2563EB' } } }
      row.getCell(3).style = excelRowStyle(index % 2 === 1)
      row.getCell(4).style = excelRowStyle(index % 2 === 1)
      row.getCell(5).style = { ...excelRowStyle(index % 2 === 1), alignment: { wrapText: true as const, vertical: 'top' as const } }
    })
  }

  return workbook
}

// ── Download Helpers ─────────────────────────────────────────
export async function downloadExcel(wb: ExcelJS.Workbook, filename: string) {
  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}



